import { resolvePlayerName } from './gameStatsHelpers.js';

export function canonicalPlayerKey(name) {
  if (typeof name !== 'string') return null;
  const trimmed = name.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

export function normalizeTeamLabel(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim().toLowerCase();
  if (!text) return null;
  if (text === 'red' || text === 'r' || text === 'team1' || text === 'home' || text === 'host' || text === '0') return 'red';
  if (text === 'blue' || text === 'b' || text === 'team2' || text === 'away' || text === 'guest' || text === '1') return 'blue';
  if (text.includes('red')) return 'red';
  if (text.includes('blue')) return 'blue';
  return null;
}

export function assignPlayerToTeam(map, name, team) {
  const normalized = normalizeTeamLabel(team);
  if (!normalized) return;
  const key = canonicalPlayerKey(name);
  if (!key || map.has(key)) return;
  const resolved = resolvePlayerName(name);
  if (!resolved) return;
  map.set(key, { name: resolved, team: normalized });
}

export function assignCollectionToTeam(map, value, team) {
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      const name = typeof entry === 'string' ? entry : resolvePlayerName(entry);
      assignPlayerToTeam(map, name, team);
    });
    return;
  }
  const name = typeof value === 'string' ? value : resolvePlayerName(value);
  assignPlayerToTeam(map, name, team);
}

export function harvestTeamsFromEvents(map, match = {}) {
  const take = (entry, team) => assignPlayerToTeam(map, resolvePlayerName(entry), team);

  const kicks = Array.isArray(match.kicks) ? match.kicks : [];
  kicks.forEach((kick) => take(kick?.player ?? kick?.nick ?? kick, kick?.team));

  const shots = Array.isArray(match.shots) ? match.shots : [];
  shots.forEach((shot) => {
    const shooterTeam = shot?.team ?? shot?.scorerTeam ?? shot?.shotTeam;
    take(shot?.player ?? shot?.scorer, shooterTeam);
    take(shot?.saver ?? shot?.savedBy, shot?.savedByTeam);
  });

  const goals = Array.isArray(match.goals) ? match.goals : [];
  goals.forEach((goal) => {
    const teamLabel = goal?.shot?.team ?? goal?.team ?? goal?.scoringTeam;
    take(goal?.scorer, teamLabel);
    take(goal?.assist?.player ?? goal?.assist, teamLabel);
    if (teamLabel) {
      const normalized = normalizeTeamLabel(teamLabel);
      const opposite = normalized === 'red' ? 'blue'
        : normalized === 'blue' ? 'red'
        : null;
      take(goal?.ownGoalPlayer, opposite);
    }
  });

  const passes = Array.isArray(match.passes) ? match.passes : [];
  passes.forEach((pass) => {
    const teamLabel = pass?.team ?? pass?.lastShot?.team ?? pass?.playerTeam;
    take(pass?.player ?? pass?.passer ?? pass?.author ?? pass?.from ?? pass?.source, teamLabel);
    take(pass?.target, teamLabel);
  });

  const saves = Array.isArray(match.saves) ? match.saves : [];
  saves.forEach((save) => take(save?.player ?? save?.keeper ?? save?.nick ?? save, save?.team));

  const duels = Array.isArray(match.duels) ? match.duels : [];
  duels.forEach((duel) => {
    take(duel?.player, duel?.team ?? duel?.winnerTeam);
    take(duel?.opponent, duel?.opponentTeam ?? duel?.loserTeam);
    take(duel?.winner, duel?.winnerTeam);
    take(duel?.loser, duel?.loserTeam);
  });

  const rebounds = Array.isArray(match.rebounds) ? match.rebounds : [];
  rebounds.forEach((rebound) => take(rebound?.player ?? rebound?.nick, rebound?.team));

  const reboundRecoveries = Array.isArray(match.reboundRecoveries) ? match.reboundRecoveries : [];
  reboundRecoveries.forEach((entry) => take(entry?.player ?? entry?.nick, entry?.team));

  const interceptions = Array.isArray(match.interceptions) ? match.interceptions : [];
  interceptions.forEach((entry) => take(entry?.player ?? entry?.nick, entry?.team));

  const simpleCollections = [
    match.clearances,
    match.clearanceRecoveries,
    match.recoveries,
    match.counterRuns,
    match.pressures,
    match.blocks,
    match.tackles
  ];
  simpleCollections.forEach((collection) => {
    if (!Array.isArray(collection)) return;
    collection.forEach((entry) => take(entry?.player ?? entry?.winner ?? entry, entry?.team ?? entry?.teamName ?? entry?.winnerTeam));
  });
}

export function inferTeamsFromMatch(match = {}) {
  const assignments = new Map();
  assignCollectionToTeam(assignments, match.redTeam, 'red');
  assignCollectionToTeam(assignments, match.redPlayers, 'red');
  assignCollectionToTeam(assignments, match.blueTeam, 'blue');
  assignCollectionToTeam(assignments, match.bluePlayers, 'blue');

  if (match.teams && typeof match.teams === 'object') {
    assignCollectionToTeam(assignments, match.teams.red || match.teams.redTeam || match.teams.home || match.teams.homeTeam || match.teams.team1, 'red');
    assignCollectionToTeam(assignments, match.teams.blue || match.teams.blueTeam || match.teams.away || match.teams.awayTeam || match.teams.team2, 'blue');
  }

  const roster = Array.isArray(match.players) ? match.players : Array.isArray(match.player) ? match.player : [];
  roster.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const name = resolvePlayerName(entry);
    const team = normalizeTeamLabel(entry.team ?? entry.side ?? entry.playerTeam ?? entry.teamKey);
    if (team) assignPlayerToTeam(assignments, name, team);
  });

  harvestTeamsFromEvents(assignments, match);

  roster.forEach((entry) => {
    const name = resolvePlayerName(entry);
    const key = canonicalPlayerKey(name);
    if (!key || assignments.has(key)) return;
    const redCount = Array.from(assignments.values()).filter((val) => val.team === 'red').length;
    const blueCount = Array.from(assignments.values()).filter((val) => val.team === 'blue').length;
    const fallbackTeam = redCount <= blueCount ? 'red' : 'blue';
    assignPlayerToTeam(assignments, name, fallbackTeam);
  });

  const red = [];
  const blue = [];
  assignments.forEach(({ name, team }) => {
    if (team === 'red') red.push(name);
    else if (team === 'blue') blue.push(name);
  });

  return {
    red: Array.from(new Set(red)).filter(Boolean),
    blue: Array.from(new Set(blue)).filter(Boolean),
    assignments
  };
}

export default inferTeamsFromMatch;
